import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, Store, Star, Zap, Check, Repeat, Bell, BellRing } from "lucide-react";
import { Sparkles } from "lucide-react";
import { api, imgUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePicks } from "@/context/PicksContext";
import StarRating from "@/components/StarRating";
import AuthModal from "@/components/AuthModal";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get("ref");
  const { user } = useAuth();
  const { isInfluencer, isPicked, addPick } = usePicks();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [authOpen, setAuthOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [address, setAddress] = useState("");
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [pickNote, setPickNote] = useState("");
  const [addingPick, setAddingPick] = useState(false);
  const [useCredit, setUseCredit] = useState(true);
  const [swapOpen, setSwapOpen] = useState(false);
  const [myListings, setMyListings] = useState([]);
  const [swapOfferId, setSwapOfferId] = useState("");
  const [swapDesc, setSwapDesc] = useState("");
  const [swapNote, setSwapNote] = useState("");
  const [proposing, setProposing] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const [waitlistBusy, setWaitlistBusy] = useState(false);

  usePageMeta(
    product && product.title ? `${product.title}${product.brand ? ` — ${product.brand}` : ""} | Beauty Trade Club` : "Shop Beauty | Beauty Trade Club",
    product && product.description ? product.description.slice(0, 155) : "Shop authentic new & gently-used beauty from verified sellers on Beauty Trade Club, with escrow-protected checkout.",
    product && product.id ? `/product/${product.id}` : ""
  );

  const load = () => api.get(`/products/${id}`).then(({ data }) => setProduct(data)).catch(() => setProduct(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    if (user && product && product.status && product.status !== "active") {
      api.get(`/products/${id}/waitlist`).then(({ data }) => setWaitlisted(data.waitlisted)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user, product?.status, id]);

  const joinWaitlist = async () => {
    if (!user) { setAuthOpen(true); return; }
    setWaitlistBusy(true);
    try {
      const { data } = await api.post(`/products/${id}/waitlist`);
      setWaitlisted(true);
      toast.success(data.message);
    } catch (e) { toast.error(apiError(e)); }
    finally { setWaitlistBusy(false); }
  };

  const buy = async () => {
    if (!user) { setAuthOpen(true); return; }
    if (!address.trim()) { toast.error("Please enter a shipping address"); return; }
    setBuying(true);
    try {
      const { data } = await api.post("/checkout", {
        product_id: id, origin_url: window.location.origin, shipping_address: address,
        ref: ref || undefined, use_store_credit: useCredit,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e));
      setBuying(false);
    }
  };

  const openSwap = () => {
    if (!user) { setAuthOpen(true); return; }
    setSwapOpen(true);
    api.get("/my/products").then(({ data }) => setMyListings((data || []).filter((p) => p.status === "active" && p.id !== id))).catch(() => {});
  };

  const proposeSwap = async () => {
    if (!swapOfferId && !swapDesc.trim()) { toast.error("Pick one of your listings or describe what you'd trade"); return; }
    setProposing(true);
    try {
      await api.post("/swaps/propose", {
        target_product_id: id,
        offer_product_id: swapOfferId || undefined,
        offer_description: swapDesc,
        note: swapNote,
      });
      toast.success("Swap offer sent! The owner will accept or decline from their dashboard. 🔄");
      setSwapOpen(false); setSwapOfferId(""); setSwapDesc(""); setSwapNote("");
    } catch (e) { toast.error(apiError(e)); }
    finally { setProposing(false); }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!user) { setAuthOpen(true); return; }
    try {
      await api.post(`/products/${id}/reviews`, { rating, comment });
      toast.success("Review posted!");
      setComment("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  if (product === false) return <div className="max-w-7xl mx-auto px-6 py-20 text-center text-neutral-500">Product not found.</div>;
  if (!product) return <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#E8956B]" /></div>;

  const onSale = product.sale_price && product.sale_price < product.price;
  const finalPrice = onSale ? product.sale_price : product.price;
  const isSold = product.status === "sold";
  const isInTrade = product.status === "in_trade";
  const isTraded = product.status === "traded";
  const isActive = product.status === "active";
  const isOwner = user && user.id === product.seller_id;

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="grid md:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-neutral-100 border border-neutral-200">
            <img data-testid="product-main-image" src={imgUrl(product.images?.[activeImg])} alt={product.title} className="w-full h-full object-cover" />
          </div>
          {product.images?.length > 1 && (
            <div className="flex gap-3 mt-4">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumb-${i}`}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === activeImg ? "border-[#E8956B]" : "border-neutral-200"}`}>
                  <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {product.boosted && (
            <div data-testid="boosted-note" className="mt-3 flex items-center gap-1.5 text-xs text-[#CE744A] font-semibold">
              <Zap size={13} className="fill-[#E8956B] text-[#E8956B]" /> Photos enhanced with AI Image Boost for a crisp, studio-quality look.
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-2 text-xs">
            <span className={`px-2 py-1 rounded font-bold tracking-wider ${product.condition === "new" ? "bg-[#E8956B] text-white" : product.condition === "swatched" ? "bg-[#CE744A] text-white" : "bg-neutral-800 text-white"}`}>
              {product.condition === "new" ? "NEW" : product.condition === "swatched" ? "SWATCHED ONCE" : "LIGHTLY USED"}
            </span>
            {isSold && <span className="px-2 py-1 rounded bg-neutral-100 border border-neutral-300 text-neutral-600 font-bold">SOLD</span>}
            {isInTrade && <span data-testid="in-trade-badge" className="px-2 py-1 rounded bg-amber-100 border border-amber-300 text-amber-700 font-bold">IN THE PROCESS</span>}
            {isTraded && <span className="px-2 py-1 rounded bg-neutral-100 border border-neutral-300 text-neutral-600 font-bold">TRADED</span>}
            {product.times_used && <span data-testid="times-used-badge" className="px-2 py-1 rounded bg-neutral-100 border border-neutral-200 text-neutral-600 font-semibold">Used: {product.times_used}</span>}
          </div>
          <h1 className="font-display text-4xl font-bold mt-4 text-neutral-900">{product.title}</h1>
          <p className="text-neutral-500 mt-1">{product.brand}</p>
          <div className="flex items-center gap-2 mt-2 text-sm text-neutral-500 flex-wrap">
            <Store size={14} /> Sold by{" "}
            <button data-testid="seller-profile-link" onClick={() => navigate(`/seller/${product.seller_id}`)} className="text-neutral-900 font-medium hover:text-[#E8956B] hover:underline transition-colors">
              {product.seller_name}
            </button>
            <ShieldCheck size={14} className="text-[#E8956B]" /> <span className="text-[#E8956B]">Verified</span>
            {product.seller_review_count > 0 && (
              <span data-testid="seller-rating" className="flex items-center gap-1 text-neutral-700">
                <Star size={14} className="text-[#E8956B]" fill="currentColor" /> {product.seller_rating}
                <span className="text-neutral-400">({product.seller_review_count} seller review{product.seller_review_count === 1 ? "" : "s"})</span>
              </span>
            )}
            {product.seller_trade_review_count > 0 && (
              <span data-testid="seller-trade-rating" className="flex items-center gap-1 text-neutral-700">
                <Repeat size={14} className="text-[#E8956B]" /> {product.seller_trade_rating}
                <span className="text-neutral-400">({product.seller_trade_review_count} trade{product.seller_trade_review_count === 1 ? "" : "s"})</span>
              </span>
            )}
          </div>

          {product.review_count > 0 && (
            <div className="flex items-center gap-2 mt-3">
              <StarRating value={product.avg_rating} size={18} />
              <span className="text-sm text-neutral-500">{product.avg_rating} · {product.review_count} reviews</span>
            </div>
          )}

          <div className="mt-5 flex items-center gap-3">
            <span className="text-4xl font-black text-neutral-900">${finalPrice.toFixed(2)}</span>
            {onSale && <span className="text-xl text-neutral-400 line-through">${product.price.toFixed(2)}</span>}
          </div>

          <p className="mt-5 text-neutral-600 leading-relaxed">{product.description}</p>

          {isInfluencer && !isSold && (
            <div data-testid="detail-add-pick" className="mt-6 rounded-xl border border-[#E8956B]/40 bg-[#E8956B]/5 p-4">
              {isPicked?.(product.id) ? (
                <div className="flex items-center gap-2 text-sm font-semibold text-[#CE744A]">
                  <Check size={16} /> This product is on your pick page. Edit the note anytime from your Dashboard.
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm font-bold text-neutral-800 mb-2">
                    <Star size={15} className="text-[#E8956B]" /> Add this to your pick page
                  </div>
                  <Textarea
                    data-testid="detail-pick-note"
                    value={pickNote}
                    onChange={(e) => setPickNote(e.target.value)}
                    placeholder="Why do you love it? (shown to shoppers — optional)"
                    className="bg-white border-neutral-300 min-h-20"
                  />
                  <button
                    data-testid="detail-add-pick-btn"
                    disabled={addingPick}
                    onClick={async () => { setAddingPick(true); const ok = await addPick(product.id, pickNote); setAddingPick(false); if (ok) setPickNote(""); }}
                    className="mt-2 inline-flex items-center gap-1.5 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-5 py-2 text-sm transition-all disabled:opacity-50"
                  >
                    {addingPick ? <Loader2 size={15} className="animate-spin" /> : <Star size={15} />} Add to my picks
                  </button>
                </>
              )}
            </div>
          )}

          {ref && !isSold && (
            <div data-testid="referral-banner" className="mt-4 flex items-center gap-2 text-sm bg-[#E8956B]/10 border border-[#E8956B]/30 rounded-xl px-4 py-2.5 text-[#8a4a29]">
              <Sparkles size={15} className="text-[#E8956B]" /> You followed an influencer pick — supporting a creator with this purchase.
            </div>
          )}

          {isActive ? (
            <div className="mt-8 space-y-3">
              <Input
                data-testid="shipping-address-input"
                placeholder="Shipping address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className="bg-neutral-50 border-neutral-300 h-12"
              />
              {user?.store_credit > 0 && (
                <label data-testid="use-store-credit-toggle" className="flex items-center gap-2 text-sm bg-green-50 border border-green-200 rounded-xl px-4 py-3 cursor-pointer">
                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} className="accent-green-600 w-4 h-4" />
                  <span className="text-green-800 font-semibold">Apply my ${user.store_credit.toFixed(2)} store credit</span>
                </label>
              )}
              <button
                data-testid="buy-now-detail-btn"
                onClick={buy}
                disabled={buying}
                className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-4 text-lg btn-glow transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {buying ? <><Loader2 size={20} className="animate-spin" /> Redirecting...</> : `Buy Now · $${finalPrice.toFixed(2)}`}
              </button>
              {!isOwner && (
                <button
                  data-testid="propose-swap-btn"
                  onClick={openSwap}
                  className="w-full border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 font-bold rounded-full py-3.5 text-base transition-all flex items-center justify-center gap-2"
                >
                  <Repeat size={18} /> Propose a Swap
                </button>
              )}
              <p className="text-xs text-neutral-400 text-center flex items-center justify-center gap-1">
                <ShieldCheck size={13} className="text-[#E8956B]" /> Escrow protected — seller is paid only after you confirm delivery.
              </p>
            </div>
          ) : isInTrade ? (
            <div className="mt-8 rounded-xl border border-amber-200 bg-amber-50 p-4 text-center" data-testid="in-trade-notice">
              <p className="text-amber-800 font-medium">🔄 This item is currently <b>in the process of a trade</b> — it's reserved until that swap completes.</p>
              {waitlisted ? (
                <div data-testid="waitlisted-state" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-4 py-2">
                  <BellRing size={16} /> You're on the waitlist — we'll email you if it's available again.
                </div>
              ) : (
                <button
                  data-testid="notify-available-btn"
                  onClick={joinWaitlist}
                  disabled={waitlistBusy}
                  className="mt-3 inline-flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-3 transition-all disabled:opacity-50"
                >
                  {waitlistBusy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} Notify me if it's available again
                </button>
              )}
            </div>
          ) : (
            <div className="mt-8 rounded-xl border border-neutral-200 bg-neutral-50/50 p-4 text-center" data-testid={isTraded ? "traded-notice" : "sold-notice"}>
              <p className="text-neutral-500 font-medium">This item has been {isTraded ? "traded" : "sold"}.</p>
              {waitlisted ? (
                <div data-testid="waitlisted-state" className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-4 py-2">
                  <BellRing size={16} /> You're on the waitlist — we'll email you if it's available again.
                </div>
              ) : (
                <button
                  data-testid="notify-available-btn"
                  onClick={joinWaitlist}
                  disabled={waitlistBusy}
                  className="mt-3 inline-flex items-center gap-2 border-2 border-[#E8956B] text-[#CE744A] hover:bg-[#E8956B]/10 font-bold rounded-full px-6 py-2.5 transition-all disabled:opacity-50"
                >
                  {waitlistBusy ? <Loader2 size={16} className="animate-spin" /> : <Bell size={16} />} Notify me if it's available again
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-16 max-w-3xl">
        <h2 className="font-display text-2xl font-bold mb-6">Reviews ({product.reviews?.length || 0})</h2>

        <form onSubmit={submitReview} className="bg-white border border-neutral-200 rounded-2xl p-5 mb-8">
          <p className="text-sm font-semibold mb-2">Leave a review</p>
          <StarRating value={rating} size={26} editable onChange={setRating} />
          <Textarea
            data-testid="review-comment-input"
            placeholder="Share your experience with this product..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="bg-neutral-50 border-neutral-300 mt-3"
          />
          <button data-testid="submit-review-btn" type="submit" className="mt-3 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2 transition-all">
            Post Review
          </button>
        </form>

        <div className="space-y-4">
          {product.reviews?.map((r) => (
            <div key={r.id} data-testid={`review-${r.id}`} className="bg-neutral-50 border border-neutral-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-sm">{r.buyer_name}</span>
                <StarRating value={r.rating} size={14} />
              </div>
              {r.comment && <p className="text-sm text-neutral-500 mt-2">{r.comment}</p>}
            </div>
          ))}
          {(!product.reviews || product.reviews.length === 0) && <p className="text-neutral-400 text-sm">No reviews yet. Be the first!</p>}
        </div>
      </div>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />

      <Dialog open={swapOpen} onOpenChange={setSwapOpen}>
        <DialogContent className="bg-white sm:max-w-lg" data-testid="swap-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Repeat size={18} className="text-[#E8956B]" /> Propose a swap for {product.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-1">
            <p className="text-sm text-neutral-500">
              Offer one of your own listings, or describe what you'd trade. If {product.seller_name} accepts, you'll each pay a small $4.99 swap fee, then ship &amp; confirm — fully escrow-protected.
            </p>
            {myListings.length > 0 && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Offer one of your listings</label>
                <div className="grid gap-2 mt-2 max-h-52 overflow-auto">
                  {myListings.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`swap-offer-${p.id}`}
                      onClick={() => setSwapOfferId(swapOfferId === p.id ? "" : p.id)}
                      className={`flex items-center gap-3 rounded-xl border-2 p-2 text-left transition-all ${swapOfferId === p.id ? "border-[#E8956B] bg-[#E8956B]/10" : "border-neutral-200 hover:border-[#E8956B]/60"}`}
                    >
                      <img src={imgUrl(p.images?.[0])} alt="" className="w-12 h-12 rounded-lg object-cover" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{p.title}</div>
                        <div className="text-xs text-neutral-500">${p.price.toFixed(2)}</div>
                      </div>
                      {swapOfferId === p.id && <Check size={16} className="text-[#E8956B]" />}
                    </button>
                  ))}
                </div>
                <div className="text-center text-xs text-neutral-400 my-2">— or —</div>
              </div>
            )}
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#E8956B]">Describe what you'd trade</label>
              <Textarea
                data-testid="swap-desc-input"
                value={swapDesc}
                onChange={(e) => setSwapDesc(e.target.value)}
                placeholder="e.g. Brand-new MAC Ruby Woo lipstick, never opened"
                className="bg-neutral-50 border-neutral-300 mt-2 min-h-20"
              />
            </div>
            <Input
              data-testid="swap-note-input"
              value={swapNote}
              onChange={(e) => setSwapNote(e.target.value)}
              placeholder="Add a friendly note (optional)"
              className="bg-neutral-50 border-neutral-300"
            />
            <button
              data-testid="submit-swap-btn"
              onClick={proposeSwap}
              disabled={proposing}
              className="w-full bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full py-3 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {proposing ? <><Loader2 size={16} className="animate-spin" /> Sending...</> : <><Repeat size={16} /> Send swap offer</>}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
